import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { getInitials } from '../utils/stringUtils';

const Header: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { isManager, selectedAccount, accounts, selectAccount, createAccount, refreshAccounts } = useAccount();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [mobileAccountPickerOpen, setMobileAccountPickerOpen] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const accountPickerRef = useRef<HTMLDivElement>(null);
  
  // Get artist accounts for the picker
  const artistAccounts = accounts.filter(a => a.type === 'artist');

  // Close account picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountPickerRef.current && !accountPickerRef.current.contains(event.target as Node)) {
        setAccountPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  if (!isAuthenticated) {
    return null;
  }

  // Determine the home link based on account type
  const homeLink = isManager ? '/manager-dashboard' : '/my-splits';

  const navLinks = [
    ...(isManager ? [{ to: '/manager-dashboard', label: 'Dashboard' }] : []),
    { to: '/my-splits', label: isManager ? 'Works' : 'My Splits' },
    { to: '/collaborators', label: 'Collaborators' },
    { to: '/create-split', label: 'Create Split' },
  ];

  return (
    <>
      <header className="bg-surface-secondary/30 backdrop-blur-sm border-b border-border-subtle sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Mobile Hamburger Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden p-2 -ml-2 text-content-secondary hover:text-content hover:bg-accent/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <Link to={homeLink} className="flex items-center hover:opacity-80 transition-opacity">
                <img src="/fulllogo.png" alt="Cotune" className="h-8 md:h-10 w-auto" />
              </Link>
              
              {/* Desktop Navigation Menu */}
              <nav className="hidden md:flex space-x-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-content-secondary hover:text-content font-medium transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
            
            {/* Desktop User Info */}
            <div className="hidden md:flex items-center gap-4">
              {/* Account Picker for Managers */}
              {isManager && artistAccounts.length > 0 && (
                <div className="relative" ref={accountPickerRef}>
                  <button
                    onClick={() => setAccountPickerOpen(!accountPickerOpen)}
                    className="flex items-center gap-2 px-3 py-2 bg-surface-secondary hover:bg-surface-tertiary border border-border-subtle rounded-lg transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#14B8A6] flex items-center justify-center text-xs text-white font-medium">
                      {getInitials(selectedAccount?.name || '?')}
                    </div>
                    <span className="text-sm text-content max-w-[120px] truncate">
                      {selectedAccount?.name || 'Select Artist'}
                    </span>
                    <svg 
                      className={`w-4 h-4 text-content-muted transition-transform ${accountPickerOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown */}
                  {accountPickerOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-surface-secondary border border-border-subtle rounded-lg shadow-xl z-[100] overflow-hidden">
                      <div className="px-3 py-2 border-b border-border-subtle">
                        <p className="text-xs text-content-muted uppercase tracking-wide font-medium">Select Artist Account</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {artistAccounts
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((account) => (
                            <button
                              key={account.id}
                              onClick={() => {
                                selectAccount(account.id);
                                setAccountPickerOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent/10 transition-colors ${
                                selectedAccount?.id === account.id ? 'bg-accent/20' : ''
                              }`}
                            >
                              <div className="w-8 h-8 rounded-full bg-[#14B8A6] flex items-center justify-center text-sm text-white font-medium flex-shrink-0">
                                {getInitials(account.name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-content truncate">{account.name}</p>
                                <p className="text-xs text-content-muted">Artist</p>
                              </div>
                              {selectedAccount?.id === account.id && (
                                <svg className="w-4 h-4 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))}
                      </div>
                      {/* Create Artist Account Button */}
                      <div className="border-t border-border-subtle px-3 py-2">
                        <button
                          onClick={() => {
                            setAccountPickerOpen(false);
                            setShowCreateAccountModal(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent/10 rounded-lg transition-colors text-[#14B8A6]"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#14B8A6]/20 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium">Create Artist Account</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={logout}
                className="px-4 py-2 bg-error/80 hover:bg-error text-content rounded-md transition-colors text-sm border border-error/30 min-h-[44px]"
              >
                Logout
              </button>
            </div>

            {/* Mobile User Avatar/Logout */}
            <button
              onClick={logout}
              className="md:hidden p-2 text-content-secondary hover:text-content hover:bg-error/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-72 bg-surface-secondary border-r border-border-subtle shadow-xl transform transition-transform duration-300 ease-out">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <img src="/fulllogo.png" alt="Cotune" className="h-8 w-auto" />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-content-secondary hover:text-content hover:bg-accent/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-border-subtle">
              <p className="text-content font-medium">{user?.name}</p>
            </div>

            {/* Account Picker for Managers (Mobile) - Collapsible */}
            {isManager && artistAccounts.length > 0 && (
              <div className="border-b border-border-subtle">
                {/* Collapsed view - shows selected account, tap to expand */}
                <button
                  onClick={() => setMobileAccountPickerOpen(!mobileAccountPickerOpen)}
                  className="w-full p-4 flex items-center justify-between hover:bg-accent/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#14B8A6] flex items-center justify-center text-sm text-white font-medium flex-shrink-0">
                      {getInitials(selectedAccount?.name || '?')}
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-content-muted uppercase tracking-wide font-medium">Creating for</p>
                      <p className="text-sm text-content font-medium truncate max-w-[160px]">
                        {selectedAccount?.name || 'Select Artist'}
                      </p>
                    </div>
                  </div>
                  <svg 
                    className={`w-5 h-5 text-content-muted transition-transform ${mobileAccountPickerOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Expanded list */}
                {mobileAccountPickerOpen && (
                  <div className="px-4 pb-4">
                    <div className="space-y-1 max-h-[30vh] overflow-y-auto">
                      {artistAccounts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((account) => (
                          <button
                            key={account.id}
                            onClick={() => {
                              selectAccount(account.id);
                              setMobileAccountPickerOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                              selectedAccount?.id === account.id 
                                ? 'bg-accent/20 border border-accent/30' 
                                : 'hover:bg-accent/10'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-[#14B8A6] flex items-center justify-center text-sm text-white font-medium flex-shrink-0">
                              {getInitials(account.name)}
                            </div>
                            <span className="text-sm text-content truncate flex-1">{account.name}</span>
                            {selectedAccount?.id === account.id && (
                              <svg className="w-4 h-4 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        ))}
                    </div>
                    {/* Create Artist Account Button (Mobile) */}
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileAccountPickerOpen(false);
                        setShowCreateAccountModal(true);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 mt-2 rounded-lg text-left hover:bg-accent/10 transition-colors text-[#14B8A6] border-t border-border-subtle pt-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#14B8A6]/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium">Create Artist Account</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Links */}
            <nav className="p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-content-secondary hover:text-content hover:bg-accent/10 rounded-lg transition-colors min-h-[48px] font-medium"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Logout Button */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border-subtle">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout();
                }}
                className="w-full px-4 py-3 bg-error/80 hover:bg-error text-content rounded-lg transition-colors text-sm border border-error/30 min-h-[48px] font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Artist Account Modal */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#14B8A6] mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Create Artist Account</h2>
              <p className="text-[#9CA3AF] text-sm">
                Enter the name for the artist account. This is the artist whose works you'll be managing.
              </p>
            </div>
            
            <div className="mb-6">
              <label htmlFor="headerNewAccountName" className="block text-sm font-medium text-[#9CA3AF] mb-2">
                Artist Name *
              </label>
              <input
                type="text"
                id="headerNewAccountName"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                className="w-full px-3 py-2 bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                placeholder="Enter artist name"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateAccountModal(false);
                  setNewAccountName('');
                }}
                className="flex-1 px-4 py-2 border border-[#3F3F46] text-white rounded-lg hover:bg-[#3F3F46] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!newAccountName.trim()) return;
                  setIsCreatingAccount(true);
                  try {
                    await createAccount({
                      name: newAccountName.trim(),
                      type: 'artist'
                    });
                    await refreshAccounts();
                    setShowCreateAccountModal(false);
                    setNewAccountName('');
                  } catch (err) {
                    console.error('Failed to create account:', err);
                  } finally {
                    setIsCreatingAccount(false);
                  }
                }}
                disabled={!newAccountName.trim() || isCreatingAccount}
                className="flex-1 px-4 py-2 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0D9488] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingAccount ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
